import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import {
  analyticsBreakdown, analyticsRealtime, analyticsStatus, analyticsSummary, formatApiError,
} from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Panel, StatCard, TrendChart, BarList, fmt } from "../../components/admin/AnalyticsWidgets";
import { ConnectPanel, ConnectionBar, CONNECT_MESSAGES } from "../../components/admin/GoogleConnect";

const RANGES = [7, 30, 90];

function errorText(err) {
  const d = err?.response?.data?.detail;
  return d?.msg || formatApiError(err);
}

function Realtime() {
  const [rt, setRt] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => analyticsRealtime().then((d) => { setRt(d); setError(""); }).catch((e) => setError(errorText(e))), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <section className="bg-neutral-900 text-neutral-100 rounded-xl p-6 sm:p-7 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-center" data-testid="analytics-realtime">
      <div>
        <p className="eyebrow text-indigo-200 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Right now
        </p>
        <p className="font-playfair text-6xl leading-none text-white" data-testid="realtime-active-users">{rt ? fmt(rt.active_users) : "—"}</p>
        <p className="text-sm text-neutral-300 mt-2">active users in the last 30 minutes</p>
        {error && <p className="text-xs text-red-300 mt-3" data-testid="realtime-error">{error}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 min-w-0">
        {[["Active pages", rt?.pages], ["Active countries", rt?.countries]].map(([label, list]) => (
          <div key={label} className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-neutral-400 mb-3">{label}</p>
            {list?.length ? (
              <ul className="space-y-2 text-sm">
                {list.map((r) => (
                  <li key={r.name} className="flex justify-between gap-3"><span className="truncate text-neutral-200">{r.name}</span><span className="font-semibold text-white">{r.users}</span></li>
                ))}
              </ul>
            ) : <p className="text-sm text-neutral-400">No one on the site right now.</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function AdminAnalytics() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadStatus = useCallback(() => analyticsStatus().then(setStatus).catch((e) => setError(errorText(e))), []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    const result = params.get("google");
    if (!result) return;
    const [kind, msg] = CONNECT_MESSAGES[result] || ["error", "Google sign-in didn't complete."];
    toast[kind](msg);
    params.delete("google");
    setParams(params, { replace: true });
  }, [params, setParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, b] = await Promise.all([analyticsSummary(), analyticsBreakdown(days)]);
      setSummary(s);
      setBreakdown(b);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { if (status?.configured) load(); }, [status, load]);

  if (!status && !error) return <div className="text-neutral-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-6xl space-y-6" data-testid="admin-analytics-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-2">
        <div>
          <p className="eyebrow mb-2">Google Analytics 4</p>
          <h1 className="font-playfair text-4xl text-neutral-900 tracking-tight">Analytics</h1>
          {status?.configured && <p className="text-xs text-neutral-500 mt-2">Property {status.property_id} · live data from Google Analytics</p>}
        </div>
        {status?.configured && (
          <button type="button" onClick={load} disabled={loading} className="self-start sm:self-auto inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neutral-300 text-sm text-neutral-700 hover:border-indigo-800 hover:text-indigo-800 disabled:opacity-60 transition-colors" data-testid="analytics-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        )}
      </header>

      {status && !status.configured && <ConnectPanel status={status} isOwner={isOwner} />}
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-4 py-3" data-testid="analytics-error">{error}</div>}

      {status?.configured && (
        <>
          <ConnectionBar status={status} isOwner={isOwner} onDisconnected={() => { setSummary(null); setBreakdown(null); setError(""); loadStatus(); }} />
          <Realtime />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="analytics-summary">
            <StatCard label="Today" period="Since midnight (property time zone)" data={summary?.today} testid="stat-today" />
            <StatCard label="This week" period="Last 7 days, including today" data={summary?.week} testid="stat-week" />
            <StatCard label="This month" period="Last 30 days, including today" data={summary?.month} testid="stat-month" />
          </div>

          <div className="flex items-center justify-between gap-4 pt-4">
            <h2 className="font-playfair text-2xl text-neutral-900">Breakdown</h2>
            <div className="inline-flex rounded-full border border-neutral-300 bg-surface p-1" data-testid="analytics-range">
              {RANGES.map((r) => (
                <button key={r} type="button" onClick={() => setDays(r)} data-testid={`analytics-range-${r}`}
                  className={`px-3.5 py-1 rounded-full text-xs font-semibold transition-colors ${days === r ? "bg-indigo-800 text-surface" : "text-neutral-600 hover:text-indigo-800"}`}>
                  {r} days
                </button>
              ))}
            </div>
          </div>

          <Panel title="Visitors and page views" subtitle={`Daily, last ${days} days`} testid="analytics-trend">
            {breakdown ? <TrendChart data={breakdown.trend} /> : <div className="h-64 bg-neutral-100 rounded-lg animate-pulse" />}
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Panel title="Top pages" subtitle="By page views" testid="analytics-pages">
              <BarList items={breakdown?.pages} valueKey="views" labelKey="path" unit="views" testid="pages-list" />
            </Panel>
            <Panel title="Traffic sources" subtitle="Channels by sessions" testid="analytics-channels">
              <BarList items={breakdown?.channels} valueKey="sessions" unit="sessions" testid="channels-list" />
              {breakdown?.sources?.length > 0 && (
                <>
                  <p className="text-xs uppercase tracking-wider text-neutral-500 mt-7 mb-3">Source / medium</p>
                  <BarList items={breakdown.sources} valueKey="sessions" testid="sources-list" />
                </>
              )}
            </Panel>
            <Panel title="Countries" subtitle="By visitors" testid="analytics-countries">
              <BarList items={breakdown?.countries} valueKey="users" unit="visitors" testid="countries-list" />
            </Panel>
            <Panel title="Devices" subtitle="By visitors" testid="analytics-devices">
              <BarList items={breakdown?.devices} valueKey="users" unit="visitors" testid="devices-list" />
            </Panel>
          </div>
          <p className="text-xs text-neutral-500">Reports refresh every 5 minutes and real-time data every minute. Google Analytics can take 24–48 hours to finish processing a day's data.</p>
        </>
      )}
    </div>
  );
}
